import { diffArrays } from "diff";
import {
  absent,
  present,
  sameFile,
  isEncryptedFile,
  type FileState,
  type Conflict,
  type Operation,
} from "./model";
type Hunk = { start: number; end: number; lines: string[] };
function hunks(base: string[], changed: string[]): Hunk[] {
  const result: Hunk[] = [];
  let offset = 0;
  let current: Hunk | null = null;
  for (const part of diffArrays(base, changed)) {
    if (!part.added && !part.removed) {
      current = null;
      offset += part.value.length;
      continue;
    }
    if (!current) {
      current = { start: offset, end: offset, lines: [] };
      result.push(current);
    }
    if (part.removed) {
      offset += part.value.length;
      current.end = offset;
    }
    if (part.added) current.lines.push(...part.value);
  }
  return result;
}
export function mergeText(
  base: string,
  local: string,
  remote: string,
): string | null {
  if (local === remote || remote === base) return local;
  if (local === base) return remote;
  const lines = base.match(/[^\n]*\n|[^\n]+$/g) ?? [];
  // Large conflicting documents are resolved explicitly rather than blocking the UI.
  if (
    lines.length > 20000 ||
    base.length + local.length + remote.length > 3 * 1024 * 1024
  )
    return null;
  const left = hunks(lines, local.match(/[^\n]*\n|[^\n]+$/g) ?? []);
  const right = hunks(lines, remote.match(/[^\n]*\n|[^\n]+$/g) ?? []);
  const combined = [...left];
  for (const r of right) {
    let duplicate = false;
    for (const l of left) {
      if (
        l.start === r.start &&
        l.end === r.end &&
        l.lines.join("") === r.lines.join("")
      ) {
        duplicate = true;
        break;
      }
      if (
        Math.max(l.start, r.start) < Math.min(l.end, r.end) ||
        l.start === r.start ||
        (l.start === l.end && l.start > r.start && l.start < r.end) ||
        (r.start === r.end && r.start > l.start && r.start < l.end)
      )
        return null;
    }
    if (!duplicate) combined.push(r);
  }
  for (const h of combined.sort((a, b) => b.start - a.start))
    lines.splice(h.start, h.end - h.start, ...h.lines);
  return lines.join("");
}
/** `opaque` files, such as age files, are never merged line by line. */
export function mergeFile(
  base: FileState,
  local: FileState,
  remote: FileState,
  opaque = false,
): FileState | null {
  if (sameFile(local, remote)) return remote;
  if (sameFile(base, local)) return remote;
  if (sameFile(base, remote)) return local;
  if (opaque || !base.exists || !local.exists || !remote.exists) return null;
  const text = mergeText(base.text, local.text, remote.text);
  return text === null ? null : present(text);
}
/**
 * `resolved` holds merges already worked out elsewhere, such as an encrypted note's,
 * merged on its decrypted text.
 */
export function reconcile(
  base: Record<string, FileState>,
  local: Record<string, FileState>,
  remote: Record<string, FileState>,
  operations: Operation[],
  resolved: Record<string, FileState> = {},
): { files: Record<string, FileState>; conflicts: Conflict[] } {
  const files: Record<string, FileState> = {},
    conflicts: Conflict[] = [],
    handled = new Set<string>();
  const addConflict = (paths: string[], reason: string): void => {
    const pick = (source: Record<string, FileState>) =>
      Object.fromEntries(paths.map((p) => [p, source[p] ?? absent]));
    conflicts.push({
      id: crypto.randomUUID(),
      paths,
      reason,
      base: pick(base),
      local: pick(local),
      remote: pick(remote),
    });
    paths.forEach((p) => {
      files[p] = local[p] ?? absent;
      handled.add(p);
    });
  };
  // Connected chains of moves must be resolved as a single structural operation.
  const groups: Set<string>[] = [];
  for (const op of operations.filter((o) => o.kind === "move")) {
    const group = new Set(op.changes.map((c) => c.path));
    for (let i = groups.length - 1; i >= 0; i--)
      if ([...groups[i]!].some((p) => group.has(p))) {
        groups[i]!.forEach((p) => group.add(p));
        groups.splice(i, 1);
      }
    groups.push(group);
  }
  for (const group of groups) {
    const paths = [...group];
    if (paths.every((p) => sameFile(local[p] ?? absent, remote[p] ?? absent))) {
      paths.forEach((p) => {
        files[p] = remote[p] ?? absent;
        handled.add(p);
      });
      continue;
    }
    if (paths.some((p) => !sameFile(base[p] ?? absent, remote[p] ?? absent)))
      addConflict(
        paths,
        "Linked move conflicts with remote source or destination.",
      );
  }
  for (const path of new Set([
    ...Object.keys(base),
    ...Object.keys(local),
    ...Object.keys(remote),
  ])) {
    if (handled.has(path)) continue;
    const merged =
      resolved[path] ??
      mergeFile(
        base[path] ?? absent,
        local[path] ?? absent,
        remote[path] ?? absent,
        isEncryptedFile(path),
      );
    if (merged === null)
      addConflict([path], "Both versions changed this path.");
    else files[path] = merged;
  }
  // Detect file/directory collisions across independently added paths.
  const presentPaths = Object.keys(files)
    .filter((p) => files[p]!.exists)
    .sort();
  for (let i = 0; i < presentPaths.length; i++)
    for (
      let j = i + 1;
      j < presentPaths.length &&
      presentPaths[j]!.startsWith(presentPaths[i]! + "/");
      j++
    ) {
      const paths = [presentPaths[i]!, presentPaths[j]!];
      if (!paths.some((p) => handled.has(p)))
        addConflict(paths, "A file occupies the destination directory.");
    }
  return { files, conflicts };
}
